import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { RiMailLine as MailIcon } from '@remixicon/react';
import { showSuccessToast } from '@/components/toast/globalToast';
import { APP_CONFIG } from '@/config/app';
import { isValidEmail } from '@/utils/emailValidator';
import { openWhatsApp, WA_MESSAGES } from '@/utils/whatsapp';
import { Button } from '@/components/ui/button';
import { InputRoot, Input, InputIcon } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import AuthCarousel from './AuthCarousel';
import SignInFooter from './SignInFooter';

const Index: React.FC = () => {
  const { user, token, signIn, isLoading } = useAuth();
  const navigate = useNavigate();
  const hasShownWelcome = useRef(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);

  // Redirect to the app once authenticated.
  useEffect(() => {
    if (user && token) {
      if (!hasShownWelcome.current) {
        showSuccessToast('Berhasil masuk!', `Selamat datang di ${APP_CONFIG.name}`);
        hasShownWelcome.current = true;
      }
      navigate(APP_CONFIG.homeRoute, { replace: true });
    }
  }, [user, token, navigate]);

  const canSubmit = !!email && isEmailValid && !!password && !isLoading;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await signIn(email, password);
    } catch (err) {
      // Error toast is handled inside the auth context.
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-bg-white-0">
      <div className="flex flex-1">
        {/* Left — form column */}
        <div className="flex flex-1 flex-col">
        <header className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <img
              src="/images/logo.png"
              width={120}
              height={32}
              alt={`${APP_CONFIG.name} logo`}
              className="h-8 w-auto"
            />
          </div>
        </header>

        <div className="flex flex-1 items-center justify-center px-4 py-6 sm:px-6">
          <div className="w-full max-w-[420px] rounded-[24px] border border-stroke-soft-200 bg-bg-white-0 p-6 shadow-card-md sm:p-8 lg:border-0 lg:shadow-none">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold tracking-tight text-text-strong-950 mb-2">
              {APP_CONFIG.loginTitle}
            </h1>
            <p className="text-sm text-text-sub-600">
              {APP_CONFIG.loginDescription}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <InputRoot invalid={!isEmailValid}>
                <InputIcon>
                  <MailIcon className="size-4" strokeWidth={1.75} />
                </InputIcon>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEmail(value);
                    setIsEmailValid(value === '' || isValidEmail(value));
                  }}
                  placeholder="nama@perusahaan.com"
                />
              </InputRoot>
              {!isEmailValid && (
                <p className="text-xs text-error-base">
                  Masukkan alamat email yang valid.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
              />
            </div>

            <div className="text-right">
              <button
                type="button"
                onClick={() => openWhatsApp(WA_MESSAGES.resetPassword)}
                className="text-sm font-semibold text-primary hover:underline"
              >
                Lupa Kata Sandi?
              </button>
            </div>

            <Button
              type="submit"
              tone="primary"
              size="xl"
              loading={isLoading}
              disabled={!canSubmit}
              className="w-full"
            >
              {isLoading ? 'Memproses…' : 'Masuk'}
            </Button>

            <p className="text-center text-sm text-text-sub-600">
              Belum punya akun?{' '}
              <button
                type="button"
                onClick={() => openWhatsApp(WA_MESSAGES.register)}
                className="font-semibold text-primary hover:underline"
              >
                Hubungi tim Dash
              </button>
            </p>
          </form>
        </div>
      </div>

        </div>

        {/* Right — testimonial carousel (lg and up) */}
        <AuthCarousel />
      </div>

      {/* Consumer-complaint footer — full width, spans both columns */}
      <SignInFooter />
    </div>
  );
};

export default Index;
